/**
 * Manual Journal on COGS Base Config (accounting domain)
 *
 * Covers ``ex-manual-journal-on-cogs-base``: POSTED manual journals with at
 * least one line on the Lightspeed COGS base account (5000-100) instead of
 * 5000-200 (COGS - Inventory Adjustments). One row per journal. Detail comes
 * straight off ``franchise_exception_tags.metadata``.
 *
 * The GL-side sibling of ``ex-cogs-monthly-incomplete`` and the cross-FP
 * ledger of what the COGS Review tool flags per-FP-per-month. Confidence is
 * a structural-first classification (the systematic ``LS COGS Adjustment``
 * monthly misposting vs the year-end "leave alone" tail); the magnitude
 * marker flags corrupt single lines (a sweep found a −$100M entry).
 */
import React from 'react'
import { Chip, Tooltip } from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import { composeRowSubtitle, datePart, textPart } from '../shared/rowSubtitle'
import { formatCurrency } from '../../../utils/inspectorFormatters'
import { formatValue } from '../../../utils/tableFormatters'

const CONFIDENCE_META: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  // amber = action needed (the migration target)
  likely_misposting: { label: 'Likely misposting', color: 'warning' },
  // green = likely fine to leave on 5000-100 (accountant year-end work)
  likely_eofy: { label: 'Likely EOFY', color: 'success' },
  // grey = needs a human to triage
  unclassified: { label: 'Unclassified', color: 'default' },
}

const renderConfidence = (value: any): React.ReactNode => {
  const meta = CONFIDENCE_META[value as string]
  if (!meta) return '-'
  return React.createElement(Chip, {
    label: meta.label,
    color: meta.color,
    size: 'small',
    variant: 'outlined',
  })
}

// Currency, but red + a warning icon when the tagger flagged the journal's
// largest single 5000-100 line as out-of-band (a likely data-entry error).
const renderTotalOnBase = (value: any, row: Record<string, any>): React.ReactNode => {
  const formatted = formatCurrency(Number(value ?? 0))
  if (!row?.suspiciously_large) return formatted
  return withTooltip(
    { title: 'Unusually large single line - check for a data-entry error' },
    React.createElement(
      'span',
      {
        style: {
          color: '#d32f2f',
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        },
      },
      React.createElement(WarningAmberIcon, { fontSize: 'inherit' }),
      formatted
    )
  )
}

// Stacks the author name over the date in one cell (Posted / Last Edited)
// so the inspector shows two columns instead of four. The date reuses the
// table's own 'date' formatter so it matches every other date cell.
const renderPostedWhen = (name: any, whenIso: any): React.ReactNode => {
  const displayName = name || '-'
  if (!whenIso) return displayName
  return React.createElement(
    'span',
    { style: { display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 } },
    React.createElement('span', null, displayName),
    React.createElement(
      'span',
      { style: { color: '#888', fontSize: '0.85em' } },
      formatValue(whenIso, { type: 'date' } as any)
    )
  )
}

const inspectorColumns: ExceptionColumnDef[] = [
  { key: 'date', label: 'Journal Date', type: 'date', contexts: ['inspector'] },
  {
    key: 'journal_label',
    label: 'Journal',
    contexts: ['inspector'],
    // Deep-links to the journal in Xero. journal_label is the number when
    // Xero assigned one, else "View in Xero"; the cell shows '-' (no link)
    // only if journal_url is missing (FP without a Xero shortcode).
    link: { field: 'journal_url', system: 'xero' },
  },
  {
    key: 'narration',
    label: 'Narration',
    align: 'left',
    contexts: ['inspector'],
    render: (value) => value || '-',
  },
  {
    key: 'created_by',
    label: 'Posted',
    align: 'left',
    contexts: ['inspector'],
    render: (_value, row) => renderPostedWhen(row.created_by, row.created_date),
  },
  {
    key: 'last_edited_by',
    label: 'Last Edited',
    align: 'left',
    contexts: ['inspector'],
    render: (_value, row) => renderPostedWhen(row.last_edited_by, row.last_edited_date),
  },
  {
    key: 'confidence',
    label: 'Confidence',
    align: 'center',
    contexts: ['inspector'],
    render: renderConfidence,
  },
  {
    key: 'lines_on_base',
    label: 'Lines on 5000-100',
    align: 'right',
    contexts: ['inspector'],
  },
  {
    key: 'total_on_base',
    label: 'Total on 5000-100',
    type: 'currency',
    align: 'right',
    contexts: ['inspector'],
    render: renderTotalOnBase,
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  { key: 'date', label: 'Journal Date', type: 'date', contexts: ['platform'] },
  {
    key: 'journal_label',
    label: 'Journal',
    contexts: ['platform'],
    link: { field: 'journal_url', system: 'xero' },
  },
  {
    key: 'narration',
    label: 'Narration',
    align: 'left',
    contexts: ['platform'],
    render: (value) => value || '-',
  },
  {
    key: 'created_by',
    label: 'Posted By',
    align: 'left',
    contexts: ['platform'],
    render: (value) => value || '-',
  },
  { key: 'created_date', label: 'Posted Date', type: 'date', contexts: ['platform'] },
  {
    key: 'last_edited_by',
    label: 'Last Edited By',
    align: 'left',
    contexts: ['platform'],
    render: (value) => value || '-',
  },
  {
    key: 'last_edited_date',
    label: 'Last Edited Date',
    type: 'date',
    contexts: ['platform'],
  },
  {
    key: 'confidence',
    label: 'Confidence',
    align: 'center',
    contexts: ['platform'],
    render: renderConfidence,
  },
  {
    key: 'lines_on_base',
    label: 'Lines on 5000-100',
    align: 'right',
    contexts: ['platform'],
  },
  {
    key: 'total_on_base',
    label: 'Total on 5000-100',
    type: 'currency',
    align: 'right',
    contexts: ['platform'],
    render: renderTotalOnBase,
  },
]

export const manualJournalOnCogsBaseConfig: ExceptionConfig = {
  codes: ['ex-manual-journal-on-cogs-base'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...platformColumns],
  defaultSortColumn: 'date',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'object_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      row.date ? datePart(row.date) : null,
      typeof row.lines_on_base === 'number'
        ? `${row.lines_on_base} line${row.lines_on_base === 1 ? '' : 's'} on 5000-100`
        : null,
      textPart(row.narration),
    ]),
}
